import React, { useState, useEffect, useRef } from 'react';
import { FaArrowLeft, FaPlay, FaStop, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';

const SyncMonitor = ({ socket, syncData, setSyncData, onBack }) => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({
    totalMessages: 0,
    transferredMessages: 0,
    skippedMessages: 0,
    errorMessages: 0,
    currentFolder: 'Initializing...',
    bytesTransferred: 0
  });
  const [status, setStatus] = useState('starting');
  const [elapsed, setElapsed] = useState(0);
  const [syncId, setSyncId] = useState(null);
  const [pollingMode, setPollingMode] = useState(false);
  const [forceRender, setForceRender] = useState(0);

  const logsEndRef = useRef(null);
  const intervalRef = useRef(null);
  const pollingRef = useRef(null);

  // Polling function for sync status
  const pollSyncStatus = async (syncId) => {
    try {
      const response = await fetch(`/api/sync/status/${syncId}`);
      if (response.ok) {
        const data = await response.json();
        console.log('Polled sync status:', data);

        // Update state with polled data
        setStatus(data.status || 'running');
        setElapsed(data.elapsed || 0);

        if (data.stats) {
          setStats(prevStats => {
            const newStats = { ...prevStats, ...data.stats };

            // Add a log entry for major updates
            if (data.stats.transferredMessages !== prevStats.transferredMessages ||
                data.stats.currentFolder !== prevStats.currentFolder) {
              setLogs(prev => [...prev, {
                timestamp: data.timestamp,
                line: `[POLLING] Progress: ${data.stats.transferredMessages}/${data.stats.totalMessages} messages processed, Current folder: ${data.stats.currentFolder}`,
                type: 'info'
              }]);
            }

            return newStats;
          });
          // Force re-render to ensure HTML updates
          setForceRender(prev => prev + 1);
        }

        // Stop polling if sync is complete
        if (data.status === 'completed' || data.status === 'error') {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        }
      }
    } catch (error) {
      console.error('Polling error:', error);
    }
  };

  // Start polling if socket fails or as backup
  const startPolling = (syncId) => {
    console.log('Starting polling mode for sync:', syncId);
    setPollingMode(true);

    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    pollingRef.current = setInterval(() => {
      pollSyncStatus(syncId);
    }, 10000); // Poll every 10 seconds
  };

  // Test function to inject dummy stats (for debugging)
  const testStatsUpdate = () => {
    console.log('Testing stats update with dummy data');
    setStats({
      totalMessages: 1000,
      transferredMessages: 250,
      skippedMessages: 5,
      errorMessages: 2,
      currentFolder: 'INBOX',
      bytesTransferred: 1024000
    });
    // Force re-render to ensure HTML updates
    setForceRender(prev => prev + 1);
    console.log('Test stats applied, forceRender triggered');
  };

  useEffect(() => {
    if (!socket && syncId) {
      // No socket, use polling mode
      startPolling(syncId);
      return;
    }

    if (!socket) return;

    // Socket event listeners
    const handleSyncStarted = (data) => {
      console.log('Sync started:', data);
      setSyncId(data.syncId);
      setStatus('running');
      setLogs(prev => [...prev, {
        timestamp: data.timestamp,
        line: `Sync started with ID: ${data.syncId}`,
        type: 'info'
      }]);

      // Start polling as backup after 5 seconds
      setTimeout(() => {
        if (status === 'running' && !pollingMode) {
          console.log('Starting backup polling mode');
          startPolling(data.syncId);
        }
      }, 5000);
    };

    const handleSyncOutput = (data) => {
      console.log('Received sync-output:', data);
      setLogs(prev => [...prev, data]);
      // Always update stats if provided, merge with current stats
      if (data.stats) {
        console.log('Updating stats from:', data.stats);
        setStats(prevStats => {
          console.log('Previous stats:', prevStats);
          const newStats = {
            ...prevStats,
            ...data.stats
          };
          console.log('New stats will be:', newStats);
          return newStats;
        });
        // Force re-render to ensure HTML updates
        setForceRender(prev => prev + 1);
      }
      setElapsed(data.elapsed || 0);
    };

    const handleSyncCompleted = (data) => {
      console.log('Sync completed:', data);
      setStatus(data.success ? 'completed' : 'error');
      setStats(data.stats || stats);
      setElapsed(data.elapsed || 0);
      setLogs(prev => [...prev, {
        timestamp: data.timestamp,
        line: `Sync completed with exit code: ${data.exitCode}`,
        type: data.success ? 'success' : 'error'
      }]);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };

    const handleSyncError = (data) => {
      console.error('Sync error:', data);
      setStatus('error');
      setLogs(prev => [...prev, {
        timestamp: data.timestamp,
        line: `Error: ${data.error}`,
        type: 'error'
      }]);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };

    socket.on('sync-started', handleSyncStarted);
    socket.on('sync-output', handleSyncOutput);
    socket.on('sync-completed', handleSyncCompleted);
    socket.on('sync-error', handleSyncError);

    // Timer for elapsed time
    if (status === 'running') {
      intervalRef.current = setInterval(() => {
        setElapsed(prev => prev + 1000);
      }, 1000);
    }

    return () => {
      socket.off('sync-started', handleSyncStarted);
      socket.off('sync-output', handleSyncOutput);
      socket.off('sync-completed', handleSyncCompleted);
      socket.off('sync-error', handleSyncError);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [socket, status, syncId]);

  // Auto-scroll to bottom of logs
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const formatElapsedTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}:${(minutes % 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
    }
    return `${minutes}:${(seconds % 60).toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = () => {
    // If completed, always show 100%
    if (status === 'completed') {
      return 100;
    }

    // If we have a proper total, calculate normally
    if (stats.totalMessages > 0) {
      const transferred = stats.transferredMessages || 0;
      const skipped = stats.skippedMessages || 0;
      const processed = transferred + skipped;
      const percentage = Math.round((processed / stats.totalMessages) * 100);
      return Math.min(100, Math.max(0, percentage));
    }

    // Progressive calculation for when we don't have totals yet
    const transferred = stats.transferredMessages || 0;
    const skipped = stats.skippedMessages || 0;
    const processed = transferred + skipped;

    // Keep a minimum progress if we're actively running and have processed messages
    if (status === 'running') {
      if (processed > 0) {
        // Show gradual progress based on processed messages, but never go below previous progress
        const calculatedProgress = Math.min(90, Math.max(10, processed / 10));
        return Math.round(calculatedProgress);
      }

      // If we're running and have moved past initialization, show at least 5%
      if (stats.currentFolder && stats.currentFolder !== 'Initializing...') {
        return 5;
      }
    }

    return 0;
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'running':
        return <FaPlay className="status-icon running" />;
      case 'completed':
        return <FaCheckCircle className="status-icon success" />;
      case 'error':
        return <FaExclamationTriangle className="status-icon error" />;
      default:
        return <FaPlay className="status-icon" />;
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  return (
    <div className="sync-monitor">
      <div className="monitor-header">
        <button className="btn btn-secondary" onClick={onBack}>
          <FaArrowLeft /> Back to Form
        </button>
        <button className="btn btn-primary" onClick={testStatsUpdate} style={{marginLeft: '10px'}}>
          Test Stats Update
        </button>
        <div className="sync-status">
          {getStatusIcon()}
          <span className="status-text">
            {status === 'starting' && 'Initializing sync...'}
            {status === 'running' && `Sync in progress...${pollingMode ? ' (Polling Mode)' : ''}`}
            {status === 'completed' && 'Sync completed successfully!'}
            {status === 'error' && 'Sync completed with errors'}
          </span>
        </div>
      </div>

      <div className="monitor-content">
        {/* Stats Grid */}
        <div className="stats-grid" key={`stats-${forceRender}`}>
          <div className="stat-card">
            <h4>📊 Messages</h4>
            <div className="stat-row">
              <span>Total:</span>
              <span className="stat-value">{stats.totalMessages.toLocaleString()}</span>
            </div>
            <div className="stat-row">
              <span>Transferred:</span>
              <span className="stat-value success">{stats.transferredMessages.toLocaleString()}</span>
            </div>
            <div className="stat-row">
              <span>Skipped:</span>
              <span className="stat-value warning">{stats.skippedMessages.toLocaleString()}</span>
            </div>
            <div className="stat-row">
              <span>Errors:</span>
              <span className="stat-value error">{stats.errorMessages.toLocaleString()}</span>
            </div>
          </div>

          <div className="stat-card">
            <h4>⏱️ Progress</h4>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${getProgressPercentage()}%` }}
              ></div>
            </div>
            <div className="progress-text">{getProgressPercentage()}%</div>
            <div className="stat-row">
              <span>Current Folder:</span>
              <span className="stat-value">{stats.currentFolder}</span>
            </div>
            <div className="stat-row">
              <span>Elapsed Time:</span>
              <span className="stat-value">{formatElapsedTime(elapsed)}</span>
            </div>
          </div>

          <div className="stat-card">
            <h4>🔗 Connection</h4>
            <div className="stat-row">
              <span>Source:</span>
              <span className="stat-value">{syncData?.config?.user1}@{syncData?.config?.host1}</span>
            </div>
            <div className="stat-row">
              <span>Destination:</span>
              <span className="stat-value">{syncData?.config?.user2}@{syncData?.config?.host2}</span>
            </div>
            <div className="stat-row">
              <span>Data Transferred:</span>
              <span className="stat-value">{formatBytes(stats.bytesTransferred)}</span>
            </div>
            <div className="stat-row">
              <span>Sync ID:</span>
              <span className="stat-value mono">{syncId?.substring(0, 8)}...</span>
            </div>
          </div>
        </div>

        {/* Live Logs */}
        <div className="logs-section">
          <h3>📋 Live Output</h3>
          <div className="logs-container">
            {logs.map((log, index) => (
              <div key={index} className={`log-line ${log.type}`}>
                <span className="log-timestamp">
                  {new Date(log.timestamp).toLocaleTimeString()}
                </span>
                <span className="log-content">{log.line}</span>
              </div>
            ))}
            <div ref={logsEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SyncMonitor;